
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Activity, FileText, CheckCircle, Clock } from 'lucide-react';
import { useBoardContext } from '@/contexts/BoardContext';
import { useScreenSize } from '@/hooks/useScreenSize';
import { useCardNavigation } from '@/hooks/useCardNavigation';
import { formatDistanceToNow } from 'date-fns';
import { useMemo } from 'react';

export const RecentActivity = () => {
  const { allCards, boards, allColumns } = useBoardContext();
  const { isMobile } = useScreenSize();
  const { navigateToCard } = useCardNavigation();

  const recentActivity = useMemo(() => {
    if (!allCards || !allColumns || !boards) return [];
    
    return allCards
      .map(card => {
        const column = allColumns.find(col => col.id === card.column_id);
        const board = boards.find(b => b.id === column?.board_id);
        
        return {
          ...card,
          boardTitle: board?.title || 'Unknown Board',
          updatedAt: new Date(card.updated_at),
          timeAgo: formatDistanceToNow(new Date(card.updated_at), { addSuffix: true })
        };
      })
      .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())
      .slice(0, 6);
  }, [allCards, boards, allColumns]);

  const getActivityIcon = (card: any) => {
    if (card.archived) {
      return <CheckCircle className="h-4 w-4 text-green-600" />;
    }
    return <FileText className="h-4 w-4 text-blue-600" />;
  };

  const getActivityType = (card: any) => {
    if (card.archived) {
      return 'Completed';
    }
    return 'Updated';
  };

  const handleCardClick = (card: any) => {
    navigateToCard(card.id, card.column_id);
  };

  return (
    <Card>
      <CardHeader className={`${isMobile ? 'pb-3' : 'pb-4'}`}>
        <CardTitle className={`${isMobile ? 'text-lg' : 'text-xl'} flex items-center gap-2`}>
          <Activity className="h-5 w-5" />
          Recent Activity
        </CardTitle>
      </CardHeader>
      <CardContent>
        {recentActivity.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No recent activity</p>
          </div>
        ) : (
          <div className="space-y-3">
            {recentActivity.map((card) => (
              <div 
                key={card.id} 
                className="flex items-start gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                onClick={() => handleCardClick(card)}
              >
                <div className={`p-2 rounded-full flex-shrink-0 ${
                  card.archived 
                    ? 'bg-green-50 dark:bg-green-950' 
                    : 'bg-blue-50 dark:bg-blue-950'
                }`}>
                  {getActivityIcon(card)}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h4 className={`font-medium truncate ${isMobile ? 'text-sm' : ''} hover:text-primary transition-colors`}>
                        {card.title}
                      </h4>
                      <p className={`text-muted-foreground truncate ${isMobile ? 'text-xs' : 'text-sm'}`}>
                        {card.boardTitle}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge variant={card.archived ? 'default' : 'secondary'} className="text-xs">
                        {getActivityType(card)}
                      </Badge>
                      <span className={`text-muted-foreground ${isMobile ? 'text-xs' : 'text-xs'}`}>
                        {card.timeAgo}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
